import type { CSSProperties, ReactNode } from "react"
import type { Family } from "@/content/families"
import styles from "@/styles/components/DeckChips.module.css"

/*
 * The family chip row — shared by the card view (switch family) and the grid
 * (filter). Same look in both; the handoff treats them as one control. The view
 * toggle sits on the right of the row.
 *
 * `onPick` is handed the clicked element so the caller can anchor the card
 * view's background wipe to it; the grid ignores it.
 */

/** One-word chip label ("Sees & Interprets" → "Sees"), as the handoff's tabs. */
const chipLabel = (family: Family) => family.short.split(" ")[0]

interface DeckChipsProps {
  families: readonly Family[]
  /** Active family number, or -1 for the "All families" chip. */
  active: number
  /** Show the trailing "All families" chip (grid view only). */
  showAll?: boolean
  onPick: (n: number, el: HTMLElement) => void
  /** The view toggle, pinned to the right of the row. */
  toggle?: ReactNode
}

export default function DeckChips({ families, active, showAll, onPick, toggle }: DeckChipsProps) {
  return (
    <div className={styles.navrow}>
      <div className={styles.chips} role="tablist" aria-label="Card families">
        {families.map((entry) => {
          const on = entry.n === active
          const style = {
            "--chip-accent": `var(--family-${entry.n}-deck-glow)`,
            "--chip-ink": `var(--family-${entry.n}-deck-ink)`,
          } as CSSProperties
          return (
            <button
              key={entry.n}
              type="button"
              role="tab"
              aria-selected={on}
              className={on ? `${styles.chip} ${styles.chipOn}` : styles.chip}
              style={style}
              onClick={(event) => onPick(entry.n, event.currentTarget)}
            >
              <img className={styles.chipGlyph} src={`/deck-glyphs/${entry.n}.png`} alt="" />
              {chipLabel(entry)}
            </button>
          )
        })}
        {showAll ? (
          <button
            type="button"
            role="tab"
            aria-selected={active < 0}
            className={
              active < 0
                ? `${styles.chip} ${styles.chipAll} ${styles.chipOn}`
                : `${styles.chip} ${styles.chipAll}`
            }
            onClick={(event) => onPick(-1, event.currentTarget)}
          >
            View All
          </button>
        ) : null}
      </div>
      {toggle ? <div className={styles.toggleSlot}>{toggle}</div> : null}
    </div>
  )
}
